import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { MoneyRegular } from '@fluentui/react-icons';
import { Column, Row } from '../common';
import { Credit } from '../../data/types';
import { InputCurrency } from '../controls/InputCurrency';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { useGuid } from '../../utilities/useGuid';


const useStyles = makeStyles({
    title: {
        width: '100%',
    },
    amountInput: {
        maxWidth: '10rem',
    },
});

export type AddPartyCreditDialogProps = Omit<Partial<DialogProps>, 'onOpenChange' | 'open' | 'children'> & {
    onSubmit?: (credit: Credit) => Promise<void> | void;
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** When set, the credit is scoped to this case. */
    caseId?: string;
    title?: string;
};

const initCredit = (creditId?: string, caseId?: string): Credit => ({
    creditId: creditId,
    caseId: caseId,
    amount: 0,
    description: '',
    dateIssued: new Date().toISOString(),
});

export const AddPartyCreditDialog = (props: AddPartyCreditDialogProps) => {
    const { onSubmit, children, open, onOpenChange, caseId, title = 'Add Party Credit' } = props;

    //Hooks
    const guid = useGuid();
    const { primaryAccount} = useAccountState();

    //State
    const [internalOpen, setInternalOpen] = useState<boolean>(false);
    const dialogOpen = open ?? internalOpen;

    //Styles
    const styles = useStyles();
    const commonStyles = useCommonStyles();

    //Form
    const form = useFormik<Credit>({
        initialValues: initCredit(guid.newGuid(), caseId),
        validate: (values) => {
            const errors: Partial<Record<keyof Credit, string>> = {};
            if (!values.amount || Number(values.amount) <= 0) {
                errors.amount = 'Amount must be greater than 0.';
            }
            if (!values.dateIssued) {
                errors.dateIssued = 'Date issued is required.';
            }
            return errors;
        },
        onSubmit: async (values, { resetForm }) => {
            const payload: Credit = {
                ...values,
                caseId: caseId ?? values.caseId,
                issuedBy: primaryAccount?.user.userId,
                amount: Number(values.amount) || 0,
            };
            if (onSubmit) await onSubmit(payload);
            resetForm({ values: initCredit(guid.newGuid(), caseId) });
            handleOpenChange(false);
        },
    });

    //Logic
    const handleOpenChange = (nextOpen: boolean) => {
        if (open === undefined) setInternalOpen(nextOpen);
        onOpenChange?.(nextOpen);
    };
    const onDialogOpenChange = (_: any, data: { open: boolean }) => {
        handleOpenChange(data.open);
    };
    const onSelectDate = (date: Date | null | undefined) => {
        form.setFieldValue('dateIssued', date ? date.toISOString() : undefined);
    };
    const clickSubmit = (e: any) => {
        e.stopPropagation();
        form.submitForm();
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        form.resetForm({ values: initCredit(undefined, caseId) });
        handleOpenChange(false);
    };

    //Effects
    useEffect(() => {
        if (dialogOpen) {
            form.resetForm({ values: initCredit(undefined, caseId) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogOpen]);

    return (
        <Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
            <>
                {!!children && (
                    <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
                )}
            </>
            <DialogSurface aria-describedby={undefined} mountNode={undefined}>
                <DialogBody>
                    <DialogTitle className={styles.title}>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <MoneyRegular />
                            </Column>
                            <Column vAlign='middle'>{title}</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column gap='small'>
                            <Row gap='medium'>
                                <Column width='auto'>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Date Issued'
                                        required
                                        validationMessage={form.errors.dateIssued as string | undefined}
                                    >
                                        <DatePicker
                                            value={
                                                form.values.dateIssued
                                                    ? new Date(form.values.dateIssued)
                                                    : undefined
                                            }
                                            onSelectDate={onSelectDate}
                                            placeholder='Select a date...'
                                            data-testid='add-party-credit-date-picker'
                                        />
                                    </Field>
                                </Column>
                                <Column width='auto'>
                                    <Field
                                        className={commonStyles.text.field}
                                        label='Amount'
                                        required
                                        validationMessage={form.errors.amount as string | undefined}
                                    >
                                        <InputCurrency
                                            name='amount'
                                            className={styles.amountInput}
                                            value={form.values.amount != null ? String(form.values.amount) : ''}
                                            onChange={form.handleChange}
                                            data-testid='add-party-credit-amount-input'
                                        />
                                    </Field>
                                </Column>
                            </Row>
                            <Field
                                className={commonStyles.text.field}
                                label='Description'
                                validationMessage={form.errors.description as string | undefined}
                            >
                                <Input
                                    name='description'
                                    value={form.values.description ?? ''}
                                    onChange={form.handleChange}
                                    placeholder='Optional description'
                                    data-testid='add-party-credit-description-input'
                                />
                            </Field>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={clickSubmit}
                                disabled={!form.isValid || !form.dirty}
                                data-testid='add-party-credit-submit-button'
                            >
                                Add Credit
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance='secondary'
                                onClick={clickCancel}
                                data-testid='add-party-credit-cancel-button'
                            >
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
